import { useState, useEffect } from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { X } from 'lucide-react';

/**
 * Wraps any element and shows an orange tooltip below on first visit.
 * Dismiss (X) persists via backend. 500ms render delay to avoid flash.
 *
 * Usage:
 *   <OnboardingTooltip id="customers_import" text="Import your customer list from a CSV file here">
 *     <button>Import CSV</button>
 *   </OnboardingTooltip>
 */
export default function OnboardingTooltip({ id, text, children }) {
  const { isTooltipVisible, dismissTooltip } = useOnboarding();
  const [show, setShow] = useState(false);

  const visible = isTooltipVisible(id);

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => setShow(true), 500);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <div className="relative inline-block">
      {children}
      {show && visible && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 animate-in fade-in slide-in-from-top-1 duration-200">
          {/* Arrow */}
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-orange-500 rotate-45" />
          <div className="relative bg-orange-500 text-white text-sm px-3 py-2 rounded-lg shadow-lg whitespace-nowrap flex items-center gap-2 max-w-xs">
            <span className="whitespace-normal">{text}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShow(false);
                dismissTooltip(id);
              }}
              className="flex-shrink-0 p-0.5 hover:bg-orange-600 rounded transition-colors"
              aria-label="Dismiss tooltip"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
